import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { FiMapPin, FiPhone, FiExternalLink, FiSearch } from "react-icons/fi";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";

export default function LocalBookstores() {
    const [bookstores, setBookstores] = useState([]);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        async function load() {
            try {
                // Fetch from both and merge
                const [bookSnap, libSnap] = await Promise.all([
                    getDocs(query(collection(db, "bookstores"), orderBy("createdAt", "desc"))),
                    getDocs(query(collection(db, "libraries"), orderBy("createdAt", "desc")))
                ]);
                
                const combined = [
                    ...bookSnap.docs.map(d => ({ ...d.data(), id: d.id })),
                    ...libSnap.docs.map(d => ({ ...d.data(), id: d.id }))
                ];

                // Deduplicate and sort
                const unique = combined
                    .filter((v, i, a) => a.findIndex(t => t.id === v.id) === i)
                    .sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));

                setBookstores(unique);

                const prodSnap = await getDocs(collection(db, "products"));
                setProducts(prodSnap.docs.map(d => ({ ...d.data(), id: d.id })));
            } catch (e) {
                console.error("Error loading bookstores:", e);
            }
            setLoading(false);
        }
        load();
    }, []);

    const filteredBookstores = bookstores.filter(store => 
        store.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        store.address.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) return (
        <div className="container" style={{ padding: "100px 20px", textAlign: "center", color: "#888" }}>
            Loading bookstores...
        </div>
    );

    return (
        <div className="local-bookstores-page">
            <SEO pageId="local-bookstores" />
            
            {/* Hero Section */}
            <div style={{ background: "var(--featured-bg, #0d4a35)", color: "var(--featured-text, white)", padding: "100px 20px 80px", textAlign: "center" }}>
                <div className="container">
                    <h1 style={{ fontSize: "3.5rem", fontFamily: "'Playfair Display', serif", marginBottom: 20 }}>Local Bookstores</h1>
                    <p style={{ fontSize: "1.2rem", maxWidth: 700, margin: "0 auto 40px", opacity: 0.9 }}>
                        Find Liliz Black's books in a bookstore near you. Support local businesses while diving into a new story.
                    </p>
                    <div style={{ position: "relative", maxWidth: 500, margin: "0 auto" }}>
                        <FiSearch style={{ position: "absolute", left: 20, top: "50%", transform: "translateY(-50%)", color: "#888" }} />
                        <input 
                            type="text" 
                            placeholder="Search by name or city..." 
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            style={{ 
                                width: "100%", padding: "18px 20px 18px 55px", borderRadius: 100, border: "none", 
                                fontSize: 16, outline: "none", boxShadow: "0 10px 30px rgba(0,0,0,0.1)", color: "#333" 
                            }} 
                        />
                    </div>
                </div>
            </div>

            <div className="container" style={{ padding: "80px 20px" }}>
                {filteredBookstores.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "60px 0", color: "#888" }}>
                        <FiMapPin size={40} style={{ marginBottom: 20, opacity: 0.3 }} />
                        <p>No bookstores found matching your search.</p>
                    </div>
                ) : (
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(350px, 1fr))", gap: 30 }}>
                        {filteredBookstores.map(store => (
                            <div key={store.id} className="bookstore-card">
                                <div style={{ display: "flex", gap: 20, marginBottom: 25 }}>
                                    {store.logoUrl ? (
                                        <img src={store.logoUrl} alt={store.name} style={{ width: 80, height: 80, borderRadius: 12, objectFit: "cover", flexShrink: 0 }} />
                                    ) : (
                                        <div style={{ width: 80, height: 80, background: "#f0f7ff", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                            <FiMapPin size={30} color="var(--accent)" />
                                        </div>
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <h3 style={{ margin: "0 0 8px", fontSize: "1.4rem", fontFamily: "'Playfair Display', serif" }}>{store.name}</h3>
                                        <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 14, marginBottom: 4 }}>
                                            <FiMapPin size={14} /> {store.address}
                                        </div>
                                        {store.phone && (
                                            <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#666", fontSize: 14, marginBottom: 4 }}>
                                                <FiPhone size={14} /> {store.phone}
                                            </div>
                                        )}
                                        {store.webpage && (
                                            <a href={store.webpage} target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--accent)", fontSize: 14, fontWeight: 600, textDecoration: "none" }}>
                                                <FiExternalLink size={14} /> Website
                                            </a>
                                        )}
                                    </div>
                                </div>

                                {store.productIds && store.productIds.length > 0 && (
                                    <div style={{ borderTop: "1px solid #eee", paddingTop: 20 }}>
                                        <h4 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#999", marginBottom: 15 }}>Books Available Here:</h4>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                                            {store.productIds.map(pid => {
                                                const book = products.find(p => p.id === pid);
                                                if (!book) return null;
                                                return (
                                                    <Link key={pid} to={`/book/${pid}`} className="store-book-tag">
                                                        <img src={book.imageUrl} alt="" style={{ width: 24, height: 32, objectFit: "cover", borderRadius: 3 }} />
                                                        <span>{book.title}</span>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <style>{`
                .bookstore-card {
                    background: white;
                    border-radius: 20px;
                    padding: 30px;
                    border: 1px solid #f0f0f0;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    display: flex;
                    flex-direction: column;
                }
                .bookstore-card:hover {
                    transform: translateY(-5px);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.08);
                    border-color: var(--green-primary);
                }
                .store-book-tag {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #f8f9fa;
                    padding: 6px 12px 6px 6px;
                    border-radius: 8px;
                    font-size: 13px;
                    color: #444;
                    text-decoration: none;
                    transition: all 0.2s;
                    border: 1px solid transparent;
                }
                .store-book-tag:hover {
                    background: white;
                    border-color: var(--green-primary);
                    color: var(--green-primary);
                    transform: scale(1.05);
                }
                @media (max-width: 768px) {
                    .local-bookstores-page h1 { fontSize: 2.5rem !important; }
                }
            `}</style>
        </div>
    );
}
